import { useState, useEffect, useRef } from "react";
import { useStableCallback } from "@/hooks/useStableCallback";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle } from "lucide-react";
import { playApprovedFeedback, playDeclinedFeedback } from "@/lib/scanSounds";

import { PaymentHeader, AppliedTenders, PaymentResult } from "@/components/payment/PaymentParts";
import { ProcessingIndicator, QuickPayView } from "@/components/payment/ProcessingViews";
import { CardButtons } from "@/components/payment/CardButtons";
import { NumpadPanel } from "@/components/payment/NumpadPanel";
import { RefundPanel } from "@/components/payment/RefundPanel";
import { ChangeDueModal } from "@/components/payment/ChangeDueModal";
import { API } from "@/apiConfig";
import { fetchTerminalSettings, terminalRoute, terminalLabel } from "@/lib/terminal";

export default function PaymentDialog({ open, onClose, cart, subtotal, tax, deposit = 0, total, employee, onSuccess, displaySettings, isRefundMode = false, mixMatchSavings = 0, buydownSavings = 0, fsEligibleTotal = 0, fsTax = 0, fsTaxableSales = 0, openCashDrawer, onAddSurcharge, onRemoveSurcharge, quickPayType = null, onClearQuickPay, ageVerifications = null, ageSkipEvents = null, verifiedCustomer = null, storeSettings = null, selectedCustomer = null, onChangePreview = null, originalRefundData = null }) {
  const [processing, setProcessing] = useState(false);
  const [processingType, setProcessingType] = useState(null);
  const [processingCountdown, setProcessingCountdown] = useState(0);
  const processingTimerRef = useRef(null);
  const processingAbortRef = useRef(null);
  const currentEcrRef = useRef("");
  const [cancelling, setCancelling] = useState(false);
  const cancelRequestedRef = useRef(false);
  const [paxSettings, setPaxSettings] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  
  // Split Tender State
  const [tenders, setTenders] = useState([]);
  const [inputStr, setInputStr] = useState("");
  
  // Change Due Modal state
  const [showChangeDueModal, setShowChangeDueModal] = useState(false);
  const [changeDueAmount, setChangeDueAmount] = useState(0);
  const [changeDueCountdown, setChangeDueCountdown] = useState(0);
  const changeDueTimerRef = useRef(null);
  const changeDueIntervalRef = useRef(null);
  
  // Guard against double-submission of completeTransaction
  const submittingRef = useRef(false);
  const drawerOpenedRef = useRef(false); // V10.18 — dedupe: don't open the drawer twice (once when change-due is confirmed, once in completeTransaction)
  const [voidingIdx, setVoidingIdx] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Payment Dialog Styles from settings
  const pdStyles = displaySettings?.payment_dialog || {};
  const headerFont = pdStyles.header_font || "Poppins";
  const headerSize = pdStyles.header_size || "20";
  const headerWeight = pdStyles.header_weight || "700";
  const headerBgColor = pdStyles.header_bg_color || "#27272A";
  const headerFontColor = pdStyles.header_font_color || "#FFFFFF";
  const totalFont = pdStyles.total_font || "Poppins";
  const totalSize = pdStyles.total_size || "30";
  const totalWeight = pdStyles.total_weight || "900";
  const totalFontColor = pdStyles.total_font_color || "#FFFFFF";
  const amountDisplayFont = pdStyles.amount_display_font || "Poppins";
  const amountDisplaySize = pdStyles.amount_display_size || "36";
  const amountDisplayWeight = pdStyles.amount_display_weight || "900";
  const amountDisplayBgColor = pdStyles.amount_display_bg_color || "#F4F4F5";
  const amountDisplayFontColor = pdStyles.amount_display_font_color || "#27272A";
  const buttonFont = pdStyles.button_font || "Poppins";
  const buttonSize = pdStyles.button_size || "14";
  const buttonWeight = pdStyles.button_weight || "700";
  const numpadFont = pdStyles.numpad_font || "Poppins";
  const numpadSize = pdStyles.numpad_size || "30";
  const numpadWeight = pdStyles.numpad_weight || "700";
  const numpadBgColor = pdStyles.numpad_bg_color || "#FFFFFF";
  const numpadFontColor = pdStyles.numpad_font_color || "#27272A";
  const quickCashFont = pdStyles.quick_cash_font || "Poppins";
  const quickCashSize = pdStyles.quick_cash_size || "14";
  const quickCashWeight = pdStyles.quick_cash_weight || "700";
  const quickCashBgColor = pdStyles.quick_cash_bg_color || "#FED7AA";
  const quickCashFontColor = pdStyles.quick_cash_font_color || "#9A3412";
  const exactCashFont = pdStyles.exact_cash_font || "Poppins";
  const exactCashSize = pdStyles.exact_cash_size || "24";
  const exactCashWeight = pdStyles.exact_cash_weight || "900";
  const exactCashBgColor = pdStyles.exact_cash_bg_color || "#16A34A";
  const exactCashFontColor = pdStyles.exact_cash_font_color || "#FFFFFF";

  // EBT Food tax adjustment
  const hasEbtFood = tenders.some(t => t.type === 'EBT_FOOD');
  const ebtTaxAdjustment = hasEbtFood ? (fsTax || 0) : 0;
  const effectiveTotal = total - ebtTaxAdjustment;
  const effectiveTax = tax - ebtTaxAdjustment;

  // Calculate remaining balance
  const paidAmount = tenders.reduce((sum, t) => sum + t.amount, 0);
  const isRefund = isRefundMode || total < 0;
  const absTotal = Math.abs(effectiveTotal);
  const absPaid = Math.abs(paidAmount);
  const remainingBalance = Math.round((isRefund ? Math.max(0, absTotal - absPaid) : Math.max(0, effectiveTotal - paidAmount)) * 100) / 100;
  const isFullyPaid = remainingBalance < 0.01;

  useEffect(() => {
    if (open) {
      fetchPaxSettings();
      setPaymentResult(null);
      setInputStr("");
      setTenders([]);
      setShowChangeDueModal(false);
      setChangeDueAmount(0);
      setChangeDueCountdown(0);
      setProcessingCountdown(0);
      setProcessing(false);
      submittingRef.current = false;
      drawerOpenedRef.current = false;
    } else {
      // V10.18 — the ChangeDueModal is portalled straight to document.body (independent of
      // Radix's own Dialog open/close), so closing this dialog must explicitly dismiss it too,
      // or it can be orphaned stuck-on-screen if the parent closes us while it's showing.
      setShowChangeDueModal(false);
      setChangeDueCountdown(0);
      // V10.20 — this dialog stays mounted between sales, so the previous sale's tenders must be
      // wiped on CLOSE too. Clearing only on open left them alive for exactly one render, and the
      // quick-pay auto-charge effect fires in that same render — it then computed
      // remaining = newTotal − previousSale'sCash and sent only that sliver (e.g. $12) to PAX,
      // recording the old cash tender as a ghost split on the new sale.
      setTenders([]);
      setInputStr("");
      setPaymentResult(null);
    }
    // Timer/abort handles (not DOM nodes): the cleanup must read whatever is current at unmount.
    const timeouts = [changeDueTimerRef];
    const intervals = [changeDueIntervalRef, processingTimerRef];
    const aborts = [processingAbortRef];
    return () => {
      timeouts.forEach((r) => { if (r.current) clearTimeout(r.current); });
      intervals.forEach((r) => { if (r.current) clearInterval(r.current); });
      aborts.forEach((r) => { if (r.current) r.current.abort(); });
    };
  }, [open]);

  // Auto-trigger quick payment (stable callback: fires once per open/quickPayType/paxSettings change)
  const runQuickPay = useStableCallback(() => {
    const overrideAmount = quickPayType === 'EBT_FOOD' ? fsEligibleTotal : null;
    processCardPayment(quickPayType, overrideAmount);
    if (onClearQuickPay) onClearQuickPay();
  });
  useEffect(() => {
    if (!open || !quickPayType || !paxSettings) return;
    // V10.20 — safety net: quick pay must only ever auto-charge a brand-new sale. If any tender
    // is already present, fall back to the normal payment UI so the cashier can see it.
    if (tenders.length > 0) { if (onClearQuickPay) onClearQuickPay(); return; }
    runQuickPay();
  }, [open, quickPayType, paxSettings, runQuickPay, tenders.length, onClearQuickPay]);

  // Keyboard support (stable handler: always sees the latest tenders/input without re-subscribing)
  const onKey = useStableCallback((e) => {
    if (e.key >= '0' && e.key <= '9') {
      e.preventDefault();
      const digit = e.key;
      setInputStr(prev => {
        if (prev.includes(".")) {
          const parts = prev.split(".");
          if (parts[1].length >= 2) return prev;
        }
        const newStr = prev + digit;
        if (newStr.includes(".")) {
          if (parseFloat(newStr) > 9999.99) return prev;
        } else {
          if (parseInt(newStr) > 999999) return prev;
        }
        return newStr;
      });
    } else if (e.key === '.') {
      e.preventDefault();
      setInputStr(prev => prev.includes(".") ? prev : (prev || "0") + ".");
    } else if (e.key === 'Backspace') {
      e.preventDefault();
      setInputStr(prev => prev.slice(0, -1));
    } else if (e.key === 'Delete' || e.key === 'c' || e.key === 'C') {
      e.preventDefault();
      setInputStr("");
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (tenders.length === 0) onClose();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      processCashPayment();
    }
  });
  useEffect(() => {
    if (!open || showChangeDueModal || processing) return;
    const handleKeyDown = (e) => onKey(e);
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, showChangeDueModal, processing, onKey]);

  const fetchPaxSettings = async () => {
    try {
      const stationId = localStorage.getItem("station_id") || "default";
      setPaxSettings(await fetchTerminalSettings(stationId));
    } catch (error) {
      console.error("Failed to load card terminal settings", error);
    }
  };

  const getInputDollarValue = () => {
    if (!inputStr) return 0;
    if (inputStr.includes(".")) return parseFloat(inputStr) || 0;
    return (parseInt(inputStr) || 0) / 100;
  };

  const getPaymentAmount = () => {
    const val = getInputDollarValue();
    return val > 0 ? Math.min(val, remainingBalance) : remainingBalance;
  };

  const processCardPayment = async (paymentType, overrideAmount = null, surchargeToAdd = 0) => {
    if (!paxSettings?.enabled) {
      toast.error("Card terminal is not enabled. Configure in Settings > Payment");
      return;
    }
    if (!paxSettings?.ip_address) {
      toast.error(`${terminalLabel(paxSettings)} address not configured`);
      return;
    }

    let paymentAmount = overrideAmount
      ? Math.min(overrideAmount, remainingBalance)
      : getPaymentAmount();
    paymentAmount += surchargeToAdd;
    paymentAmount = Math.round(paymentAmount * 100) / 100;
    if (paymentAmount <= 0) {
      toast.error("Invalid payment amount");
      return;
    }

    const timeoutSeconds = 300; // 5 min — match backend, no auto-cancel
    setProcessing(true);
    setProcessingType(paymentType);
    setPaymentResult(null);
    setProcessingCountdown(0); // No countdown — wait for terminal

    // No auto-abort timer — terminal handles cancel via physical button

    const token = localStorage.getItem("token");
    const stationId = localStorage.getItem("station_id") || "default";
    const abortController = new AbortController();
    processingAbortRef.current = abortController;
    cancelRequestedRef.current = false;
    setCancelling(false);

    try {
      toast.info(`Processing $${paymentAmount.toFixed(2)} ${paymentType.replace('_', ' ')}...`);
      const ecrRef = `txn-${Date.now()}`;
      currentEcrRef.current = ecrRef;
      const paxResponse = await axios.post(
        `${API}/${terminalRoute(paxSettings)}/payment?station_id=${stationId}`,
        { amount: paymentAmount, payment_type: paymentType, transaction_id: ecrRef },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 310000, signal: abortController.signal }
      );

      setPaymentResult(paxResponse.data);

      if (paxResponse.data.approved) {
        try { playApprovedFeedback(storeSettings); } catch (e) { console.warn("approved sound", e); }
        const isPartial = !!paxResponse.data.partial;
        const takenAmount = Math.round(((isPartial && paxResponse.data.approved_amount != null) ? paxResponse.data.approved_amount : paymentAmount) * 100) / 100;
        const newTender = {
          type: paymentType, amount: takenAmount, approved: true,
          requested_amount: paymentAmount, partial: isPartial,
          transaction_id: ecrRef, trans_num: paxResponse.data.trans_num || '',
          authCode: paxResponse.data.auth_code || '', message: isPartial ? 'PARTIALLY APPROVED' : 'APPROVED',
          card_type: paxResponse.data.card_type || '', last4: paxResponse.data.last4 || '',
          card_number: paxResponse.data.card_number || '', ref_num: paxResponse.data.ref_num || '',
          trace_number: paxResponse.data.trace_number || '', batch_number: paxResponse.data.batch_number || '',
          entry_method: paxResponse.data.entry_method || '', transaction_type: paxResponse.data.transaction_type || '',
          app_name: paxResponse.data.app_name || '', aid: paxResponse.data.aid || '',
          tsi: paxResponse.data.tsi || '', tc: paxResponse.data.tc || '', tvr: paxResponse.data.tvr || '',
          ebt_balance: paxResponse.data.ebt_balance != null ? Number(paxResponse.data.ebt_balance) : null
        };

        const updatedTenders = [...tenders, newTender];
        setTenders(updatedTenders);
        setInputStr("");
        if (isPartial) {
          const due = Math.round((paymentAmount - takenAmount) * 100) / 100;
          toast.warning(`PARTIALLY APPROVED — $${takenAmount.toFixed(2)} charged, $${due.toFixed(2)} still due`);
        } else {
          toast.success(`$${takenAmount.toFixed(2)} ${paymentType.replace('_', ' ')} APPROVED!`);
        }

        const newPaidAmount = updatedTenders.reduce((sum, t) => sum + t.amount, 0);
        const hasEbtInUpdated = updatedTenders.some(t => t.type === 'EBT_FOOD');
        const adjTotal = hasEbtInUpdated ? total - (fsTax || 0) : total;
        if (!isPartial && newPaidAmount >= adjTotal - 0.009) {
          // Auto-complete with error handling and retry
          const autoComplete = (attempt = 1) => {
            submittingRef.current = false; // Reset in case stuck
            completeTransaction(updatedTenders).catch(err => {
              console.error(`Auto-complete attempt ${attempt} failed:`, err);
              submittingRef.current = false;
              if (attempt < 3) {
                setTimeout(() => autoComplete(attempt + 1), 1500);
              }
            });
          };
          setTimeout(() => autoComplete(1), 1000);
        }
      } else {
        try { playDeclinedFeedback(storeSettings); } catch (e) { console.warn("declined sound", e); }
        toast.error(paxResponse.data.decline_reason || paxResponse.data.message || "Payment declined");
      }
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED' || abortController.signal.aborted) {
        const label = paymentType.replace('_', ' ');
        if (cancelRequestedRef.current) {
          toast.error(`${label} payment cancelled from POS — confirm the PIN pad shows cancelled`);
          setPaymentResult({ approved: false, message: 'Cancelled from POS' });
        } else {
          toast.error(`${label} payment timed out - cancelled`);
          setPaymentResult({ approved: false, message: 'Payment timed out - auto-cancelled' });
        }
      } else {
        const errorMsg = error.response?.data?.detail || error.message || "Payment failed";
        toast.error(errorMsg);
        setPaymentResult({ approved: false, message: errorMsg });
      }
      if (onRemoveSurcharge) onRemoveSurcharge();
    } finally {
      if (processingTimerRef.current) clearInterval(processingTimerRef.current);
      setProcessing(false);
      setProcessingType(null);
      setProcessingCountdown(0);
      setCancelling(false);
      processingAbortRef.current = null;
    }
  };

  // Cancel the card prompt from the POS: POSLink A14 to the PIN pad; the pending sale then comes
  // back ABORTED. If the terminal stays silent for 10 s we drop the request locally.
  const cancelCardPayment = async () => {
    if (!processing || cancelling) return;
    const controller = processingAbortRef.current;
    cancelRequestedRef.current = true;
    setCancelling(true);
    const token = localStorage.getItem("token");
    const stationId = localStorage.getItem("station_id") || "default";
    try {
      const r = await axios.post(`${API}/${terminalRoute(paxSettings)}/cancel?station_id=${stationId}&ref_id=${encodeURIComponent(currentEcrRef.current || "")}`, null, { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 });
      if (!r.data?.ok) toast.warning(r.data?.message || "Terminal did not confirm the cancel");
    } catch (error) {
      toast.warning(error.response?.data?.detail || "Could not reach the terminal to cancel");
    }
    setTimeout(() => { if (processingAbortRef.current === controller && controller) controller.abort(); }, 10000);
  };

  const processCashPayment = async (cashAmount = null) => {
    const effectiveRemaining = remainingBalance;
    const paymentAmount = cashAmount || (getInputDollarValue() > 0 ? getInputDollarValue() : effectiveRemaining);

    if (paymentAmount < 0.01 && !isRefund) {
      // If already fully paid, just complete the transaction
      if (isFullyPaid && tenders.length > 0) {
        completeTransaction(tenders).catch(() => { submittingRef.current = false; });
        return;
      }
      toast.error("Please enter a cash amount");
      return;
    }

    const appliedAmount = Math.round((isRefund ? effectiveRemaining : Math.min(paymentAmount, remainingBalance)) * 100) / 100;
    const changeAmount = isRefund ? 0 : Math.round(Math.max(0, paymentAmount - remainingBalance) * 100) / 100;

    const newTender = {
      type: 'CASH', amount: appliedAmount,
      tendered: isRefund ? -appliedAmount : paymentAmount,
      change: changeAmount, approved: true,
      message: isRefund ? 'REFUND' : (changeAmount > 0 ? `Change: $${changeAmount.toFixed(2)}` : 'PAID')
    };

    const updatedTenders = [...tenders, newTender];
    setTenders(updatedTenders);
    setInputStr("");

    const newPaidAmount = updatedTenders.reduce((sum, t) => sum + t.amount, 0);
    const hasEbtInCash = updatedTenders.some(t => t.type === 'EBT_FOOD');
    const adjTotalCash = hasEbtInCash ? total - (fsTax || 0) : total;
    const targetAmount = isRefund ? Math.abs(total) : adjTotalCash;

    if (newPaidAmount >= targetAmount - 0.009) {
      if (changeAmount > 0 && !isRefund) {
        setChangeDueAmount(changeAmount);
        setShowChangeDueModal(true);
        // V10.18 — the sale is already fully paid (cash covers total + change) the instant this
        // modal appears, so open the drawer now rather than waiting for the cashier to dismiss
        // it. completeTransaction() (fired later by handleCompleteWithChange) skips re-opening
        // it via drawerOpenedRef.
        if (openCashDrawer && !drawerOpenedRef.current) {
          drawerOpenedRef.current = true;
          openCashDrawer().then(ok => {
            if (!ok) toast.error("Cash drawer failed to open — check printer settings");
          });
        }
        // Customer display: show cash tendered + change the instant the cashier enters it
        if (onChangePreview) onChangePreview({ amountTendered: updatedTenders.reduce((s, t) => s + (t.tendered || t.amount), 0), changeDue: changeAmount });

        const duration = displaySettings?.change_due_modal_duration || 5;
        setChangeDueCountdown(duration);

        if (duration > 0) {
          if (changeDueIntervalRef.current) clearInterval(changeDueIntervalRef.current);
          changeDueIntervalRef.current = setInterval(() => {
            setChangeDueCountdown(prev => {
              if (prev <= 1) { clearInterval(changeDueIntervalRef.current); return 0; }
              return prev - 1;
            });
          }, 1000);

          changeDueTimerRef.current = setTimeout(() => {
            if (changeDueIntervalRef.current) clearInterval(changeDueIntervalRef.current);
            handleCompleteWithChange(updatedTenders, changeAmount);
          }, duration * 1000);
        }
      } else {
        completeTransaction(updatedTenders);
      }
    } else {
      toast.success(`$${appliedAmount.toFixed(2)} CASH applied. Remaining: $${(remainingBalance - appliedAmount).toFixed(2)}`);
    }
  };

  const handleCompleteWithChange = (finalTenders, change) => {
    if (changeDueTimerRef.current) clearTimeout(changeDueTimerRef.current);
    if (changeDueIntervalRef.current) clearInterval(changeDueIntervalRef.current);
    setShowChangeDueModal(false);
    setChangeDueCountdown(0);
    completeTransaction(finalTenders, change);
  };

  const completeTransaction = async (finalTenders, changeDue = 0) => {
    // Prevent double-submission (race between setTimeout and COMPLETE button click)
    if (submittingRef.current) return;
    submittingRef.current = true;

    // Filter out phantom $0 tenders (edge case from race conditions)
    const validTenders = finalTenders.filter(t => t.amount >= 0.01);
    if (validTenders.length === 0) {
      submittingRef.current = false;
      toast.error("No valid payment tenders");
      return;
    }

    const token = localStorage.getItem("token");
    const hasEbtInFinal = validTenders.some(t => t.type === 'EBT_FOOD');
    const finalTaxAdjustment = hasEbtInFinal ? (fsTax || 0) : 0;
    const finalTax = tax - finalTaxAdjustment;
    let finalTotal = total - finalTaxAdjustment;

    // --- Surcharge fix: detect if surcharge was applied but not yet in cart props (stale closure) ---
    let finalItems = [...cart];
    const surchargeInCart = cart.some(item => item.sku === "CC-DB-SURCHARGE");
    const tenderTotal = validTenders.reduce((sum, t) => sum + t.amount, 0);
    const surchargeGap = Math.round((tenderTotal - changeDue - finalTotal) * 100) / 100;

    if (!surchargeInCart && surchargeGap >= 0.01 && !isRefundMode && total >= 0) {
      // Surcharge was added to PAX payment but cart props are stale — inject it
      finalItems.push({
        product_id: `cc-db-surcharge-${Date.now()}`,
        sku: "CC-DB-SURCHARGE",
        product_name: "CC/DB Surcharge",
        price: surchargeGap,
        retail_price: surchargeGap,
        quantity: 1,
        subtotal: surchargeGap,
        tax_1: false, tax_2: false, foodstamp: false, deposit: false,
        category: "Surcharge",
        is_surcharge: true,
      });
      finalTotal = Math.round((finalTotal + surchargeGap) * 100) / 100;
    }

    try {
      const transactionResponse = await axios.post(
        `${API}/transactions`,
        {
          employee_id: employee.id, employee_name: employee.name,
          items: finalItems, subtotal: finalTotal - finalTax, tax: finalTax, total: finalTotal,
          payment_method: validTenders.length > 1 ? 'split' : validTenders[0].type.toLowerCase(),
          tenders: validTenders,
          amount_tendered: validTenders.reduce((sum, t) => sum + (t.tendered || t.amount), 0),
          change_due: changeDue,
          is_refund: isRefundMode || total < 0,
          mix_match_savings: mixMatchSavings, buydown_savings: buydownSavings,
          station_id: localStorage.getItem("station_id") || "default",
          ebt_tax_exempt: finalTaxAdjustment,
          fs_tax_exempt_sales: hasEbtInFinal ? (fsTaxableSales || 0) : 0,
          fs_eligible_total: hasEbtInFinal ? (fsEligibleTotal || 0) : 0,
          age_verifications: ageVerifications && ageVerifications.length ? ageVerifications : null,
          age_skip_events: ageSkipEvents && ageSkipEvents.length ? ageSkipEvents : null,
          customer_id: selectedCustomer?.id || null,
          customer_phone: selectedCustomer?.phone || null,
          original_transaction_number: (isRefundMode && originalRefundData?.transaction_number) ? originalRefundData.transaction_number : null,
          original_auth_codes: (isRefundMode && originalRefundData?.auth_codes?.length) ? originalRefundData.auth_codes : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Transaction completed!");
      // V10.18 — open the drawer ONLY now that the whole transaction (every split tender) is
      // confirmed complete and saved — never mid-split, and never at all if the final tender
      // mix has no cash in it (pure card/EBT sales never touch the drawer).
      const hasCash = validTenders.some(t => t.type === 'CASH' && t.amount > 0);
      if (hasCash && openCashDrawer && !drawerOpenedRef.current) {
        drawerOpenedRef.current = true;
        openCashDrawer().then(ok => {
          if (!ok) toast.error("Cash drawer failed to open — check printer settings");
        });
      }
      const primaryMethod = validTenders.length > 1 ? 'split' : (validTenders[0].type === 'CASH' ? 'cash' : 'card');
      onSuccess(primaryMethod, validTenders.reduce((sum, t) => sum + (t.tendered || t.amount), 0), changeDue, transactionResponse.data.transaction_number, validTenders);
    } catch (error) {
      submittingRef.current = false; // Allow retry on failure
      toast.error(error.response?.data?.detail || "Failed to save transaction");
    }
  };

  const removeTender = (index) => {
    const tender = tenders[index];
    if (tender.type === 'CASH') { setTenders(prev => prev.filter((_, i) => i !== index)); return; }
    voidCardTender(index);
  };

  // Void an approved card tender on the terminal (partial approval the customer can't finish, etc.)
  const voidCardTender = async (index) => {
    const tender = tenders[index];
    if (!tender || tender.type === 'CASH' || voidingIdx !== null) return false;
    setVoidingIdx(index);
    const token = localStorage.getItem("token");
    const stationId = localStorage.getItem("station_id") || "default";
    try {
      toast.info(`Voiding $${tender.amount.toFixed(2)} ${tender.type.replace('_', ' ')} on the terminal...`);
      const res = await axios.post(`${API}/${terminalRoute(paxSettings)}/void?station_id=${stationId}`, {
        amount: tender.amount, payment_type: tender.type, transaction_id: tender.transaction_id,
        trans_num: tender.trans_num, auth_code: tender.authCode, ref_num: tender.ref_num,
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 90000 });
      if (res.data?.approved) {
        setTenders(prev => prev.filter((_, i) => i !== index));
        setPaymentResult(null);
        toast.success(`$${tender.amount.toFixed(2)} card payment voided`);
        return true;
      }
      toast.error(`Void failed: ${res.data?.message || 'terminal did not approve'} — void it on the terminal`);
    } catch (e) {
      toast.error(`Void failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setVoidingIdx(null);
    }
    return false;
  };

  const cardTenders = tenders.filter(t => t.type !== 'CASH');
  const cardCharged = cardTenders.reduce((s, t) => s + t.amount, 0);

  // Cancel with a card already charged: void every card tender first, then close.
  const voidAllAndClose = async () => {
    setShowCancelConfirm(false);
    for (let i = tenders.length - 1; i >= 0; i--) {
      if (tenders[i].type !== 'CASH') {
        const ok = await voidCardTender(i);
        if (!ok) return;
      }
    }
    if (onRemoveSurcharge) onRemoveSurcharge();
    onClose();
  };

  const handleNumpad = (digit) => {
    setInputStr(prev => {
      if (prev.includes(".")) {
        const parts = prev.split(".");
        if (parts[1].length >= 2) return prev;
      }
      const newStr = prev + digit;
      if (newStr.includes(".")) {
        if (parseFloat(newStr) > 9999.99) return prev;
      } else {
        if (parseInt(newStr) > 999999) return prev;
      }
      return newStr;
    });
  };

  const enteredAmount = getInputDollarValue();
  const isPaxEnabled = paxSettings?.enabled && paxSettings?.ip_address;
  const minAmount = paxSettings?.min_credit_debit_amount || 0;
  const surchargeAmount = paxSettings?.cc_db_surcharge || 0;
  const hasSurchargeInCart = cart.some(item => item.sku === "CC-DB-SURCHARGE");
  const belowMin = minAmount > 0 && remainingBalance < minAmount && !hasSurchargeInCart;

  const getQuickCashAmounts = () => {
    const amounts = [];
    const ceil = Math.ceil(remainingBalance);
    if (ceil > 0 && ceil <= 100) amounts.push(ceil);
    [5, 10, 20, 50, 100].forEach(amt => {
      if (amt >= remainingBalance && !amounts.includes(amt)) amounts.push(amt);
    });
    const sorted = amounts.sort((a, b) => a - b).slice(0, 6);
    while (sorted.length < 6) sorted.push(null);
    return sorted;
  };

  const handleClose = () => {
    if (processing || voidingIdx !== null) return;
    // V10.18 — once Change Due is showing, the sale is already fully paid and the drawer has
    // already opened: Escape/outside-click must NOT be able to drop the sale un-recorded.
    // "Done" (which calls handleCompleteWithChange) is the only way out of this state.
    if (showChangeDueModal) return;
    if (cardTenders.length > 0 && !isFullyPaid) { setShowCancelConfirm(true); return; }
    if (onRemoveSurcharge) onRemoveSurcharge();
    onClose();
  };

  const timeoutSeconds = paxSettings?.timeout_seconds || 60;

  // Style bundles for sub-components
  const buttonStyles = { buttonFont, buttonSize, buttonWeight };
  const numpadStyles = {
    amountDisplayFont, amountDisplaySize, amountDisplayWeight, amountDisplayBgColor, amountDisplayFontColor,
    numpadFont, numpadSize, numpadWeight, numpadBgColor, numpadFontColor,
    quickCashFont, quickCashSize, quickCashWeight, quickCashBgColor, quickCashFontColor,
    exactCashFont, exactCashSize, exactCashWeight, exactCashBgColor, exactCashFontColor
  };
  const modalStyles = { headerFont, headerWeight, totalFont, totalSize, totalWeight, exactCashFont, exactCashSize, exactCashWeight };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-[540px] p-0 overflow-hidden" style={{ fontFamily: `${headerFont}, sans-serif` }}>
          <DialogTitle className="sr-only">Payment</DialogTitle>

          <PaymentHeader
            isRefund={isRefund} absTotal={absTotal} effectiveTotal={effectiveTotal}
            ebtTaxAdjustment={ebtTaxAdjustment} tenders={tenders} absPaid={absPaid}
            remainingBalance={remainingBalance}
            headerFont={headerFont} headerSize={headerSize} headerWeight={headerWeight}
            headerBgColor={headerBgColor} headerFontColor={headerFontColor}
            totalFont={totalFont} totalSize={totalSize} totalWeight={totalWeight} totalFontColor={totalFontColor}
          />

          <div className="px-3 py-2 space-y-1.5">
            {/* Quick Pay Mode */}
            {quickPayType && !paymentResult && !showChangeDueModal && (
              <QuickPayView processing={processing} processingType={processingType}
                processingCountdown={processingCountdown} timeoutSeconds={timeoutSeconds} paymentResult={null}
                onCancel={cancelCardPayment} cancelling={cancelling} />
            )}
            {quickPayType && paymentResult && (
              <PaymentResult paymentResult={paymentResult} remainingBalance={remainingBalance} compact />
            )}

            {/* Normal Payment UI */}
            {!quickPayType && (
              <>
                <AppliedTenders tenders={tenders} removeTender={removeTender} voidingIdx={voidingIdx} />
                <PaymentResult paymentResult={paymentResult} remainingBalance={remainingBalance} />

                {processing && (
                  <ProcessingIndicator processingType={processingType}
                    processingCountdown={processingCountdown} timeoutSeconds={timeoutSeconds}
                    onCancel={cancelCardPayment} cancelling={cancelling} />
                )}

                {!isFullyPaid && !processing && (
                  <>
                    {isRefund ? (
                      <RefundPanel absTotal={absTotal} isPaxEnabled={isPaxEnabled}
                        processCashPayment={processCashPayment} processCardPayment={processCardPayment} />
                    ) : (
                      <>
                        <NumpadPanel
                          enteredAmount={enteredAmount} remainingBalance={remainingBalance}
                          handleNumpad={handleNumpad} setInputStr={setInputStr}
                          processCashPayment={processCashPayment}
                          getQuickCashAmounts={getQuickCashAmounts}
                          handleClose={handleClose} tenders={tenders}
                          styles={numpadStyles}
                          cardButtonsSlot={
                            <CardButtons
                              isPaxEnabled={isPaxEnabled} paxSettings={paxSettings}
                              remainingBalance={remainingBalance} belowMin={belowMin}
                              surchargeAmount={surchargeAmount} fsEligibleTotal={fsEligibleTotal}
                              processCardPayment={processCardPayment} onAddSurcharge={onAddSurcharge}
                              styles={buttonStyles}
                            />
                          }
                        />
                      </>
                    )}
                  </>
                )}

                {isFullyPaid && !processing && !showChangeDueModal && (
                  <Button
                    className="w-full h-16 bg-green-600 hover:bg-green-700 text-white text-xl font-bold rounded-xl"
                    onClick={() => completeTransaction(tenders)}
                  >
                    <CheckCircle className="w-7 h-7 mr-2" />
                    COMPLETE TRANSACTION
                  </Button>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {showChangeDueModal && (
        <ChangeDueModal
          changeDueAmount={changeDueAmount} changeDueCountdown={changeDueCountdown}
          modalDuration={displaySettings?.change_due_modal_duration || 5}
          onDone={() => handleCompleteWithChange(tenders, changeDueAmount)}
          styles={modalStyles}
        />
      )}

      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent data-testid="cancel-card-charged-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Card already charged ${cardCharged.toFixed(2)}</AlertDialogTitle>
            <AlertDialogDescription>
              The customer's card has been charged but the sale isn't paid in full (${remainingBalance.toFixed(2)} still due).
              Cancelling will VOID the card payment{cardTenders.length > 1 ? "s" : ""} on the terminal so the customer is not charged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-card-keep-btn">Keep paying</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={voidAllAndClose} data-testid="cancel-card-void-btn">
              Void card & cancel sale
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
